/**
 * Seller Success Stories Page
 * Route: /success-stories
 * Features: Real seller testimonials showing MemAds classifieds value
 */

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { Star, MapPin, ArrowRight, Quote, TrendingUp, Users, ShieldCheck } from 'lucide-react';

const stories = [
  {
    id: 'story-1',
    name: 'Ramesh Patil',
    city: 'Mumbai, Maharashtra',
    avatar: 'RP',
    avatarColor: 'bg-rose-500',
    category: 'Electronics',
    headline: 'Sold ₹2.5 Lakh of electronics in 30 days during home relocation',
    story:
      'When our family moved from a 2BHK to a 3BHK, I had years of accumulated electronics to sell. I listed everything on MemAds — two smartphones, a gaming console, LED TV, washing machine, and more. The detailed listing format and fast response to inquiries made all the difference. Within 30 days, I had completed 14 transactions worth over ₹2.5 lakh. The buyers were genuine, transactions were smooth, and I never had a dispute.',
    rating: 5,
    itemsSold: 14,
    earnedRs: '₹2,52,000',
    timeframe: '30 days',
  },
  {
    id: 'story-2',
    name: 'Preethi Subramaniam',
    city: 'Bangalore, Karnataka',
    avatar: 'PS',
    avatarColor: 'bg-indigo-500',
    category: 'Furniture',
    headline: 'Furnished entire flat buying used furniture for just ₹28,000',
    story:
      'Moving to Bangalore for a new job, I was on a tight budget. Instead of renting furniture at ₹4,000/month, I spent 2 weekends shopping on MemAds. I bought a sofa for ₹7,000, a bed frame for ₹4,500, dining table for ₹5,000, and a wardrobe for ₹8,000. Everything was in excellent condition. Two years later, when I moved cities, I resold all of it for ₹18,000. My total furnishing cost for 2 years was effectively ₹10,000. No furniture rental could match that.',
    rating: 5,
    itemsBought: 8,
    savedRs: '₹78,000',
    timeframe: 'vs. 2 years of renting',
  },
  {
    id: 'story-3',
    name: 'Deepak Sharma',
    city: 'Delhi, NCR',
    avatar: 'DS',
    avatarColor: 'bg-emerald-500',
    category: 'Automobiles',
    headline: 'Found a perfect first car — verified, documented, and ₹3 lakh below market',
    story:
      'As a first-time car buyer, I was anxious about getting cheated. I used MemAds to find a 2-year-old Honda City in Delhi and compared 6 similar listings before approaching this seller. The listing had complete details — RC, insurance status, service history, and genuine photos. I got an independent mechanic to inspect it, and everything checked out. Bought it ₹3 lakh below what dealers were quoting for comparable cars. The RC transfer was smooth too.',
    rating: 5,
    savedRs: '₹3,00,000',
    timeframe: 'vs. dealer price',
  },
  {
    id: 'story-4',
    name: 'Ananya Krishnan',
    city: 'Chennai, Tamil Nadu',
    avatar: 'AK',
    avatarColor: 'bg-purple-500',
    category: 'Mobiles',
    headline: 'Upgraded to iPhone 15 by selling old devices — paid nothing extra',
    story:
      'I wanted to upgrade from my iPhone 12 to an iPhone 15, but couldn\'t justify the full price. I listed my iPhone 12, old iPad, and a pair of AirPods on MemAds. Within a week, all three were sold for a combined ₹62,000. I then bought a used iPhone 15 in mint condition with original box and bill from another MemAds seller for ₹58,000. Effectively upgraded for free — with ₹4,000 left over! The IMEI check and meeting in a safe public place made me feel secure throughout.',
    rating: 5,
    savedRs: '₹40,000+',
    timeframe: 'vs. buying new',
  },
  {
    id: 'story-5',
    name: 'Mohammed Asif',
    city: 'Hyderabad, Telangana',
    avatar: 'MA',
    avatarColor: 'bg-amber-500',
    category: 'Home Appliances',
    headline: 'Small business owner: Equipped entire office for under ₹50,000',
    story:
      'Starting a small coaching institute, I needed to equip 2 classrooms and a small office on a shoestring budget. MemAds was a game-changer. I found a set of 20 plastic chairs, 5 tables, a projector, a whiteboard, a refrigerator, and even an AC unit — all used, all functional, all at 40–60% of new prices. Total spend: ₹48,500. My accountant estimated I saved over ₹1.2 lakh compared to buying new. The institute has been running for 18 months now.',
    rating: 5,
    savedRs: '₹1,20,000+',
    timeframe: 'vs. new prices',
  },
  {
    id: 'story-6',
    name: 'Kavitha Reddy',
    city: 'Pune, Maharashtra',
    avatar: 'KR',
    avatarColor: 'bg-pink-500',
    category: 'Books & Education',
    headline: 'Sold 200+ engineering textbooks and funded a semester abroad',
    story:
      'After completing my engineering degree, I had 4 years of textbooks, reference materials, and notes. I photographed each one, listed them in batches by semester on MemAds, and priced them at 30% of original price. Junior students were eager buyers. Over 6 weeks, I sold over 200 books and materials, earning ₹34,000. That money funded my visa fees and initial expenses for a short-term course abroad. I never expected college books to be so valuable to the right buyers.',
    rating: 5,
    itemsSold: 200,
    earnedRs: '₹34,000',
    timeframe: '6 weeks',
  },
];

const stats = [
  { label: 'Listings Posted Monthly', value: '50,000+', icon: <TrendingUp className="h-5 w-5" /> },
  { label: 'Verified Sellers', value: '10,000+', icon: <ShieldCheck className="h-5 w-5" /> },
  { label: 'Happy Buyers & Sellers', value: '5,00,000+', icon: <Users className="h-5 w-5" /> },
];

const SuccessStoriesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-background" id="success-stories-page">
      <SEO
        title="Seller Success Stories — Real Buyers & Sellers on MemAds"
        description="Read real success stories from MemAds users who saved money, sold faster, and found great deals on cars, mobiles, furniture, and more across India."
        canonical="/success-stories"
      />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-primary/80 py-16 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(225,29,72,0.2),transparent_60%)]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-white transition mb-6">
            ← Back to Home
          </Link>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs text-white/80 mb-4">
            <Star className="h-3.5 w-3.5 text-amber-400 fill-amber-400" /> Real stories from real users
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-white mb-4 leading-tight">
            Success Stories from MemAds Buyers & Sellers
          </h1>
          <p className="text-base text-white/70 max-w-2xl mx-auto leading-relaxed">
            Every day, thousands of people across India save money, clear clutter, and find great deals on MemAds. Here are some of their stories — in their own words.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="bg-card border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-8 grid grid-cols-3 gap-4">
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="flex justify-center mb-1 text-primary">{stat.icon}</div>
              <div className="text-2xl font-extrabold text-foreground">{stat.value}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Stories */}
      <main className="max-w-4xl mx-auto px-4 py-12 space-y-8">
        {stories.map((story) => (
          <article
            key={story.id}
            id={story.id}
            className="bg-card border border-border rounded-2xl p-6 shadow-sm hover:shadow-md transition"
          >
            <div className="flex items-start gap-4">
              {/* Avatar */}
              <div className={`w-12 h-12 rounded-2xl ${story.avatarColor} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                {story.avatar}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="font-bold text-foreground">{story.name}</span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {story.city}
                  </span>
                  <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                    {story.category}
                  </span>
                </div>
                {/* Stars */}
                <div className="flex gap-0.5 mb-3">
                  {Array.from({ length: story.rating }).map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <h2 className="font-semibold text-foreground text-sm mb-3 leading-snug">{story.headline}</h2>
                <div className="relative">
                  <Quote className="absolute -top-1 -left-1 h-4 w-4 text-muted-foreground/30" />
                  <p className="text-sm text-muted-foreground leading-relaxed pl-4">{story.story}</p>
                </div>
                {/* Metrics */}
                <div className="flex flex-wrap gap-3 mt-4">
                  {story.earnedRs && (
                    <div className="bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded-lg px-3 py-1.5 text-xs">
                      <span className="font-bold text-green-700 dark:text-green-400">{story.earnedRs}</span>
                      <span className="text-green-600 dark:text-green-500"> earned</span>
                    </div>
                  )}
                  {story.savedRs && (
                    <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg px-3 py-1.5 text-xs">
                      <span className="font-bold text-blue-700 dark:text-blue-400">{story.savedRs}</span>
                      <span className="text-blue-600 dark:text-blue-500"> saved {story.timeframe}</span>
                    </div>
                  )}
                  {story.itemsSold && (
                    <div className="bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 rounded-lg px-3 py-1.5 text-xs">
                      <span className="font-bold text-purple-700 dark:text-purple-400">{story.itemsSold}</span>
                      <span className="text-purple-600 dark:text-purple-500"> items sold in {story.timeframe}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}

        {/* CTA */}
        <section className="bg-gradient-to-r from-primary/10 to-rose-500/10 border border-primary/20 rounded-2xl p-8 text-center mt-8">
          <Star className="h-8 w-8 text-amber-400 fill-amber-400 mx-auto mb-3" />
          <h2 className="text-xl font-bold text-foreground mb-2">Start Your Own Success Story</h2>
          <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
            Whether you're buying or selling, MemAds connects you with genuine people across India. Free to list. No commissions. Just real local deals.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/post"
              id="success-stories-post-ad-cta"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-md hover:brightness-105 transition"
            >
              Post Your First Ad <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/listings"
              id="success-stories-browse-cta"
              className="inline-flex items-center gap-2 rounded-xl border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground hover:bg-accent transition"
            >
              Browse Listings
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SuccessStoriesPage;
